import type {
  ResourceKind,
  ResourceLedger,
  ResourceLedgerEvidence,
  ResourceLifetime,
  ResourceReleaseReason,
  ResourceReleaseResult,
} from "@afar-research/shared/resourceLedger";
import * as Context from "effect/Context";
import * as Cause from "effect/Cause";
import * as Effect from "effect/Effect";
import * as Exit from "effect/Exit";
import type * as Fiber from "effect/Fiber";
import type * as Scope from "effect/Scope";

const RESOURCE_IDENTITY_PREFIX = "client-runtime/";

export const clientTransportEnvironmentOwner = (environmentId: string): string =>
  `environment:${environmentId}`;

export interface ClientTransportResourceRegistration {
  readonly ownerIdentity: string;
  readonly kind: Extract<
    ResourceKind,
    "fiber" | "listener" | "ssh-forward" | "subscription" | "timer" | "websocket"
  >;
  readonly identity: string;
  readonly lifetime: Extract<ResourceLifetime, "connection" | "retained-owner">;
}

export interface ClientTransportResourceLease {
  readonly release: (reason: ResourceReleaseReason) => ResourceReleaseResult | undefined;
}

export interface ClientTransportResourcesService {
  readonly register: (
    registration: ClientTransportResourceRegistration,
  ) => ClientTransportResourceLease;
}

const noopResources: ClientTransportResourcesService = {
  register: () => ({ release: () => undefined }),
};

export class ClientTransportResources extends Context.Reference<ClientTransportResourcesService>(
  "@afar-research/client-runtime/connection/resources/ClientTransportResources",
  { defaultValue: () => noopResources },
) {}

export const makeClientTransportResources = (input: {
  readonly ledger: ResourceLedger;
}): ClientTransportResourcesService => {
  let sequence = 0;

  return {
    register(registration) {
      sequence += 1;
      const owner = {
        kind: "client-runtime",
        identity: registration.ownerIdentity,
      } as const;
      const resource = {
        kind: registration.kind,
        identity: `${RESOURCE_IDENTITY_PREFIX}${encodeURIComponent(registration.ownerIdentity)}/${registration.kind}/${encodeURIComponent(registration.identity)}/${sequence}`,
      } as const;
      const result = input.ledger.register({
        owner,
        resource,
        lifetime: registration.lifetime,
      });
      if (result.kind !== "registered") {
        throw new Error(`Client transport resource ${resource.identity} was already registered.`);
      }
      let active = true;
      return {
        release(reason) {
          if (!active) return undefined;
          active = false;
          return input.ledger.release({ owner, resource, reason });
        },
      };
    },
  };
};

export const acquireClientTransportResource = (
  resources: ClientTransportResourcesService,
  registration: ClientTransportResourceRegistration,
): Effect.Effect<ClientTransportResourceLease, never, Scope.Scope> =>
  Effect.acquireRelease(
    Effect.sync(() => resources.register(registration)),
    (lease) =>
      Effect.sync(() => {
        lease.release("owner-disposal");
      }),
  );

export const trackClientTransportEffect = <A, E, R>(
  resources: ClientTransportResourcesService,
  registration: ClientTransportResourceRegistration,
  effect: Effect.Effect<A, E, R>,
  options?: {
    readonly successReason?: ResourceReleaseReason;
    readonly interruptionReason?: ResourceReleaseReason;
  },
): Effect.Effect<A, E, R> =>
  Effect.acquireUseRelease(
    Effect.sync(() => resources.register(registration)),
    () => effect,
    (lease, exit) =>
      Effect.sync(() => {
        const reason = Exit.isSuccess(exit)
          ? (options?.successReason ?? "close")
          : Cause.hasInterrupts(exit.cause)
            ? (options?.interruptionReason ?? "interruption")
            : "failure";
        lease.release(reason);
      }),
  );

export const forkClientTransportSubscription = <A, E, R>(
  resources: ClientTransportResourcesService,
  registration: Omit<ClientTransportResourceRegistration, "kind">,
  effect: Effect.Effect<A, E, R>,
): Effect.Effect<Fiber.Fiber<A, E>, never, R | Scope.Scope> =>
  trackClientTransportEffect(
    resources,
    { ...registration, kind: "fiber", identity: `${registration.identity}-consumer` },
    trackClientTransportEffect(resources, { ...registration, kind: "subscription" }, effect, {
      interruptionReason: "owner-disposal",
    }),
    { interruptionReason: "owner-disposal" },
  ).pipe(Effect.forkScoped);

interface ClientTransportBaselineEntry {
  readonly identity: string;
  readonly count: number;
}

export interface ClientTransportBaseline {
  readonly activeCount: number;
  readonly resources: readonly ClientTransportBaselineEntry[];
}

const baselineIdentity = (entry: ResourceLedgerEvidence["resources"][number]): string => {
  if (
    entry.owner.kind !== "client-runtime" ||
    !entry.resource.identity.startsWith(RESOURCE_IDENTITY_PREFIX)
  ) {
    return JSON.stringify({
      owner: entry.owner,
      resource: entry.resource,
      lifetime: entry.lifetime,
    });
  }
  const segments = entry.resource.identity.split("/");
  return JSON.stringify({
    owner: entry.owner,
    kind: entry.resource.kind,
    semanticIdentity: segments.slice(3, -1).join("/"),
    lifetime: entry.lifetime,
  });
};

export const captureClientTransportBaseline = (
  evidence: ResourceLedgerEvidence,
): ClientTransportBaseline => {
  const counts = new Map<string, number>();
  for (const entry of evidence.resources) {
    if (entry.release.state !== "active") continue;
    const identity = baselineIdentity(entry);
    counts.set(identity, (counts.get(identity) ?? 0) + 1);
  }
  return {
    activeCount: evidence.activeCount,
    resources: [...counts.entries()]
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([identity, count]) => ({ identity, count })),
  };
};

export const matchesClientTransportBaseline = (
  evidence: ResourceLedgerEvidence,
  baseline: ClientTransportBaseline,
): boolean => {
  const current = captureClientTransportBaseline(evidence);
  return (
    current.activeCount === baseline.activeCount &&
    current.resources.length === baseline.resources.length &&
    current.resources.every(
      (entry, index) =>
        entry.identity === baseline.resources[index]?.identity &&
        entry.count === baseline.resources[index]?.count,
    )
  );
};
